export interface Project {
  id: string;
  name: string;
  code: string;
  address: string;
  customer: string;
  generalContractor: string;
  subcontractor: string;
  techSupervision: string;
  description?: string;
  blocks_count?: number;
  total_zones?: number;
}

export interface BuildingBlock {
  id: string;
  projectId: string;
  name: string;
  code: string;
  floorsCount: number;
  sortOrder: number;
}

export interface Floor {
  id: string;
  blockId: string;
  number: number;
  name: string;
  sortOrder: number;
}

export interface Zone {
  id: string;
  blockId: string;
  floorId: string;
  type: 'floor_hall' | 'riser' | 'switchroom_grsh' | 'vru_room' | 'apartment_zone';
  code: string;
  name: string;
  status: 'not_started' | 'in_progress' | 'done' | 'issue';
  completionPercentage: number;
  floor_number?: number;
  floor_name?: string;
  block_name?: string;
  open_downtimes_count?: number;
  total_reports_count?: number;
}

export interface Team {
  id: string;
  code: string;
  name: string;
  foremanName: string;
  foremanPhone?: string;
  specialty: 'cables' | 'trays' | 'panels' | 'wiring' | 'universal';
  status: 'active' | 'inactive';
  active_workers_count?: number;
}

export type RoleHierarchy = 'foreman' | 'brigadier' | 'master' | 'helper';

export interface CountryInfo {
  code: string;
  name: string;
  flag: string;
}

export const COUNTRIES: Record<string, CountryInfo> = {
  uzbekistan: { code: 'uzbekistan', name: 'Узбекистан', flag: '🇺🇿' },
  kyrgyzstan: { code: 'kyrgyzstan', name: 'Киргизия', flag: '🇰🇬' },
  turkey: { code: 'turkey', name: 'Турция', flag: '🇹🇷' },
  russia: { code: 'russia', name: 'Россия', flag: '🇷🇺' },
  azerbaijan: { code: 'azerbaijan', name: 'Азербайджан', flag: '🇦🇿' },
  kazakhstan: { code: 'kazakhstan', name: 'Казахстан', flag: '🇰🇿' },
  belarus: { code: 'belarus', name: 'Белоруссия', flag: '🇧🇾' },
  moldova: { code: 'moldova', name: 'Молдова', flag: '🇲🇩' },
};

export interface Worker {
  id: string;
  teamId?: string;
  team_name?: string;
  fullName: string;
  position: string;
  roleHierarchy?: RoleHierarchy;
  rank?: number;
  citizenship?: string;
  passportNumber?: string;
  ebGroup?: 'II' | 'III' | 'IV' | 'V';
  ebVoltageLimit?: 'up_to_1000V' | 'above_1000V';
  ebExamDate?: string;
  ebNextExamDate?: string;
  ebProtocolNumber?: string;
  heightAccess?: boolean | number;
  heightNextExamDate?: string;
  patentNumber?: string;
  patentExpiryDate?: string;
  patentExpiry?: string;
  registrationAddress?: string;
  registrationExpiryDate?: string;
  qrCodeHash: string;
  photoUrl?: string;
  phone?: string;
  status: 'active' | 'vacation' | 'blocked';
  days_remaining?: number;
}

export interface WorkType {
  id: string;
  code: string;
  name: string;
  unit: string;
  category: string;
  defaultNormPerUnit: number;
  rdCode?: string;
}

export interface Material {
  id: string;
  code: string;
  name: string;
  unit: string;
  manufacturer: string;
  specification?: string;
  defaultCertificateId?: string;
  cert_number?: string;
  certificate_title?: string;
  cert_valid_until?: string;
}

export interface Certificate {
  id: string;
  certNumber: string;
  docType: 'certificate_conformity' | 'fire_safety' | 'quality_passport' | 'test_report';
  title: string;
  manufacturer: string;
  validFrom: string;
  validUntil: string;
  fileUrl?: string;
  status: 'active' | 'expired';
  materials_linked?: number;
}

export interface AosrAct {
  id: string;
  actNumber: string;
  act_number?: string;
  projectId: string;
  blockId: string;
  floorId: string;
  zoneId: string;
  title: string;
  workTypeId: string;
  startDate: string;
  start_date?: string;
  endDate: string;
  end_date?: string;
  projectCode: string;
  projectDrawings: string;
  status: 'draft' | 'submitted' | 'remarks' | 'signed';
  techSupervisionRemarks?: string;
  signedAt?: string;
  createdByPto: string;
  createdAt: string;
  workName?: string;
  work_name?: string;
  workLocation?: string;
  work_location?: string;
  volume?: number;
  unit?: string;
  materialsUsed?: string;
  materials_used?: string;
  certificateNumber?: string;
  certificate_number?: string;
  repSupervision?: string;
  rep_supervision?: string;
  work_type_name?: string;
  work_unit?: string;
  zone_name?: string;
  floor_name?: string;
  block_name?: string;
  project_name?: string;
}

export interface Ks6aEntry {
  id: string;
  projectId: string;
  blockId: string;
  zoneId: string;
  workTypeId: string;
  projectPlannedVolume: number;
  foremanFactVolume: number;
  ptoAosrVolume: number;
  techsupSignedVolume: number;
  ks2ClosedVolume: number;
  unit: string;
  pricePerUnit: number;
  work_type_name?: string;
  work_type_code?: string;
  zone_name?: string;
  zone_code?: string;
  block_name?: string;
}

export interface DynamicReportSchema {
  id: string;
  name: string;
  category: string;
  fields_config_json: string;
  isActive: boolean;
}

export type DowntimeReasonCategory = 
  | 'walls_not_plastered'
  | 'grsh_flooded'
  | 'ventilation_collision'
  | 'no_temporary_power'
  | 'material_delay'
  | 'other';

export const DowntimeReasonCategoryEnum = {
  WALLS_NOT_PLASTERED: 'walls_not_plastered',
  GRSH_FLOODED: 'grsh_flooded',
  VENTILATION_COLLISION: 'ventilation_collision',
  NO_TEMPORARY_POWER: 'no_temporary_power',
  MATERIAL_DELAY: 'material_delay',
  OTHER: 'other',
} as const;

export interface CheckinWorker {
  id: string;
  checkinId: string;
  workerId: string;
  worker_name?: string;
  isPresent: boolean;
  assignedZoneId?: string;
  safetyBriefingDone: boolean;
  notes?: string;
}

export interface DailyCheckin {
  id: string;
  date: string;
  teamId: string;
  team_name?: string;
  foremanName: string;
  blockId: string;
  block_name?: string;
  assignedZoneId?: string;
  zone_name?: string;
  plannedSummary: string;
  totalWorkersPresent: number;
  status: 'draft' | 'submitted';
  createdAt: string;
  workers?: CheckinWorker[];
}

export interface MaterialConsumption {
  id: string;
  reportId: string;
  reportWorkItemId: string;
  materialId: string;
  material_name?: string;
  calculatedVolume: number;
  actualVolume: number;
  deviationReason?: string;
  unit: string;
}

export interface ReportWorkItem {
  id: string;
  reportId: string;
  workTypeId: string;
  work_type_name?: string;
  volumeCompleted: number;
  unit: string;
  photoUrlsJson: string;
  photoUrls?: string[];
  materials?: MaterialConsumption[];
}

export interface Downtime {
  id: string;
  reportId?: string;
  checkinId?: string;
  zoneId: string;
  zone_name?: string;
  block_name?: string;
  floor_name?: string;
  reasonCategory: DowntimeReasonCategory;
  description: string;
  photoUrlsJson: string;
  photoUrls?: string[];
  hoursLost: number;
  status: 'open' | 'in_progress' | 'resolved';
  createdAt: string;
}

export interface DailyReport {
  id: string;
  checkinId?: string;
  date: string;
  teamId: string;
  team_name?: string;
  foremanName: string;
  blockId: string;
  block_name?: string;
  floorId: string;
  floor_name?: string;
  zoneId: string;
  zone_name?: string;
  status: 'draft' | 'submitted' | 'verified_pto' | 'included_in_aosr';
  notes?: string;
  createdAt: string;
  items?: ReportWorkItem[];
  items_count?: number;
  total_volume?: number;
  downtime?: Downtime;
}
